// src/pages/ClientsPage.tsx

import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  subscribeToClientsWithContacts,
  addClient,
  updateClient,
  deleteClient,
  addContact,
  updateContact,
  deleteContact,
  archiveClient,
  activateClient,
} from '@/lib/clients';
import { getPlanFeatures, isOverLimit } from '@/lib/planFeatures';
import { ClientModal } from '@/components/ClientModal';
import { ContactModal } from '@/components/ContactModal';
import { UpgradeModal } from '@/components/UpgradeModal';
import type { ClientWithContacts, Contact, Client } from '@/types';
import { Search, Plus, Users, Mail, Phone, MapPin, Briefcase, User, Archive, RotateCcw, Lock } from 'lucide-react';

export const ClientsPage = () => {
  const { user } = useAuth();
  const features = getPlanFeatures(user);
  
  const [clients, setClients] = useState<ClientWithContacts[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientWithContacts | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [loading, setLoading] = useState(true);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | undefined>(undefined);
  const [editingContact, setEditingContact] = useState<Contact | undefined>(undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  
  // 🆕 アクティブ数
  const [activeClientCount, setActiveClientCount] = useState(0);

  useEffect(() => {
    if (!user) return;

    const unsubscribe = subscribeToClientsWithContacts(user.uid, (data) => {
      setClients(data);
      // アクティブ数をカウント
      const activeCount = data.filter(c => c.status === 'active' || !c.status).length;
      setActiveClientCount(activeCount);
      setLoading(false);
    }, statusFilter);

    return () => unsubscribe();
  }, [user, statusFilter]);

  useEffect(() => {
    if (selectedClient) {
      const updated = clients.find((c) => c.id === selectedClient.id);
      if (updated) {
        setSelectedClient(updated);
      } else {
        setSelectedClient(null);
      }
    }
  }, [clients]);

  const filteredClients = clients.filter((client) => {
    const query = searchQuery.toLowerCase();
    return (
      client.name.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query) ||
      client.contacts.some(
        (contact) =>
          contact.name.toLowerCase().includes(query) ||
          contact.email?.toLowerCase().includes(query)
      )
    );
  });

  // 🆕 クライアント追加（制限チェック付き）
  const handleAddClient = async () => {
    if (!user) return;
    
    // Freeプランの制限チェック
    if (isOverLimit(user, 'maxActiveClients', activeClientCount)) {
      setUpgradeFeature('クライアント無制限登録');
      setShowUpgradeModal(true);
      return;
    }
    
    setEditingClient(undefined);
    setIsClientModalOpen(true);
  };

  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setIsClientModalOpen(true);
  };

  const handleDeleteClient = async (clientId: string) => {
    if (!user) return;
    if (!window.confirm('この取引先を削除しますか？関連する担当者も削除されます。')) return;
    
    setIsProcessing(true);
    try {
      await deleteClient(user.uid, clientId);
      if (selectedClient?.id === clientId) {
        setSelectedClient(null);
      }
      alert('削除しました');
    } catch (error) {
      console.error('Delete client error:', error);
      alert('削除に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // 🆕 アーカイブ処理
  const handleArchiveClient = async (clientId: string) => {
    if (!user) return;
    if (!window.confirm('この取引先をアーカイブしますか？')) return;
    
    setIsProcessing(true);
    try {
      await archiveClient(user.uid, clientId);
      alert('アーカイブしました');
    } catch (error) {
      console.error('Archive client error:', error);
      alert('アーカイブに失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // 🆕 アクティブに戻す処理（制限チェック付き）
  const handleActivateClient = async (clientId: string) => {
    if (!user) return;
    
    // Freeプランの制限チェック
    if (isOverLimit(user, 'maxActiveClients', activeClientCount)) {
      setUpgradeFeature('クライアント無制限登録');
      setShowUpgradeModal(true);
      return;
    }
    
    setIsProcessing(true);
    try {
      await activateClient(user.uid, clientId);
      alert('アクティブに戻しました');
    } catch (error) {
      console.error('Activate client error:', error);
      alert('アクティブに戻すのに失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClientSubmit = async (data: Partial<Client>) => {
    if (!user) return;
    setIsProcessing(true);
    try {
      if (editingClient) {
        await updateClient(user.uid, editingClient.id, data);
        alert('更新しました');
      } else {
        await addClient(user.uid, { ...data, status: 'active' });
        alert('登録しました');
      }
      setIsClientModalOpen(false);
      setEditingClient(undefined);
    } catch (error) {
      console.error('Client submit error:', error);
      alert(editingClient ? '更新に失敗しました' : '登録に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // 🆕 担当者追加（制限チェック付き）
  const handleAddContact = async () => {
    if (!selectedClient || !user) return;
    
    // Freeプランの制限チェック
    const contactCount = selectedClient.contacts.length;
    if (isOverLimit(user, 'maxContactsPerClient', contactCount)) {
      setUpgradeFeature('担当者無制限登録');
      setShowUpgradeModal(true);
      return;
    }
    
    setEditingContact(undefined);
    setIsContactModalOpen(true);
  };

  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setIsContactModalOpen(true);
  };

  const handleDeleteContact = async (contactId: string) => {
    if (!user) return;
    if (!window.confirm('この担当者を削除しますか？')) return;
    
    setIsProcessing(true);
    try {
      await deleteContact(user.uid, contactId);
      alert('削除しました');
    } catch (error) {
      console.error('Delete contact error:', error);
      alert('削除に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleContactSubmit = async (data: Partial<Contact>) => {
    if (!user) return;
    setIsProcessing(true);
    try {
      if (editingContact) {
        await updateContact(user.uid, editingContact.id, data);
        alert('更新しました');
      } else {
        await addContact(user.uid, data);
        alert('登録しました');
      }
      setIsContactModalOpen(false);
      setEditingContact(undefined);
    } catch (error) {
      console.error('Contact submit error:', error);
      alert(editingContact ? '更新に失敗しました' : '登録に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // 🆕 制限表示用
  const maxClients = features.maxActiveClients;
  const maxContacts = features.maxContactsPerClient;
  const isClientLimitReached = maxClients !== 'unlimited' && activeClientCount >= maxClients;
  const isContactLimitReached = selectedClient && maxContacts !== 'unlimited' && selectedClient.contacts.length >= maxContacts;

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* ヘッダー */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
            取引先管理
          </h1>
          <p className="text-gray-600 mt-2">取引先と担当者を管理します</p>
          
          {/* 🆕 制限表示 */}
          {maxClients !== 'unlimited' && (
            <p className="text-sm text-gray-500 mt-1">
              アクティブ取引先: {activeClientCount} / {maxClients}
              {isClientLimitReached && (
                <span className="ml-2 text-amber-600 font-medium">（上限に達しています）</span>
              )}
            </p>
          )}
        </div>
        
        {/* 🆕 新規追加ボタン（制限時はグレー） */}
        <button
          onClick={handleAddClient}
          className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 shadow-md flex items-center gap-2 ${
            isClientLimitReached
              ? 'bg-gray-300 text-gray-500 hover:bg-gray-400'
              : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'
          }`}
        >
          {isClientLimitReached ? <Lock className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          新規取引先
        </button>
      </div>

      {/* 🆕 ステータスフィルター */}
      <div className="mb-4 flex items-center gap-2 bg-gray-100 p-1 rounded-xl w-fit">
        {[
          { value: 'active', label: 'アクティブ' },
          { value: 'archived', label: 'アーカイブ' },
          { value: 'all', label: 'すべて' },
        ].map((status) => (
          <button
            key={status.value}
            onClick={() => setStatusFilter(status.value as any)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              statusFilter === status.value
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {status.label}
          </button>
        ))}
      </div>

      {/* メインコンテンツ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 左カラム: 取引先一覧 */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {/* 検索 */}
          <div className="p-4 border-b bg-gray-50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="取引先・担当者を検索..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* 一覧 */}
          <div className="divide-y divide-gray-200 max-h-[calc(100vh-300px)] overflow-y-auto">
            {filteredClients.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>取引先がありません</p>
                <p className="text-sm mt-1">新規取引先を追加してください</p>
              </div>
            ) : (
              filteredClients.map((client) => (
                <button
                  key={client.id}
                  onClick={() => setSelectedClient(client)}
                  className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                    selectedClient?.id === client.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 truncate">{client.name}</h3>
                        {/* 🆕 ステータスバッジ */}
                        {client.status === 'archived' && (
                          <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded">
                            アーカイブ
                          </span>
                        )}
                      </div>
                      {client.email && (
                        <p className="text-sm text-gray-500 truncate flex items-center gap-1 mt-1">
                          <Mail className="w-3 h-3" />
                          {client.email}
                        </p>
                      )}
                      {client.phone && (
                        <p className="text-sm text-gray-500 truncate flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {client.phone}
                        </p>
                      )}
                    </div>
                    <div className="ml-3 flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-600">{client.contacts.length}</span>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* 右カラム: 詳細 + 担当者 */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {selectedClient ? (
            <>
              {/* 取引先詳細 */}
              <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-blue-100">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-bold text-gray-900">{selectedClient.name}</h2>
                    {selectedClient.status === 'archived' && (
                      <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded">
                        アーカイブ
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleEditClient(selectedClient)}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                      title="編集"
                    >
                      編集
                    </button>
                    {/* 🆕 アーカイブ/アクティブ切り替え */}
                    {selectedClient.status === 'archived' ? (
                      <button 
                        onClick={() => handleActivateClient(selectedClient.id)}
                        className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                        title="アクティブに戻す"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    ) : (
                      <button 
                        onClick={() => handleArchiveClient(selectedClient.id)}
                        className="p-2 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors"
                        title="アーカイブ"
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                    )}
                    <button 
                      onClick={() => handleDeleteClient(selectedClient.id)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                      title="削除"
                    >
                      削除
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {selectedClient.email && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-4 h-4" />
                      <a href={`mailto:${selectedClient.email}`} className="hover:text-blue-600">
                        {selectedClient.email}
                      </a>
                    </div>
                  )}
                  {selectedClient.phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4" />
                      <a href={`tel:${selectedClient.phone}`} className="hover:text-blue-600">
                        {selectedClient.phone}
                      </a>
                    </div>
                  )}
                  {selectedClient.address && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span>{selectedClient.address}</span>
                    </div>
                  )}
                  {selectedClient.notes && (
                    <div className="mt-3 p-3 bg-white rounded-lg">
                      <p className="text-gray-700 text-sm whitespace-pre-wrap">{selectedClient.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* 担当者一覧 */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      担当者 ({selectedClient.contacts.length})
                    </h3>
                    {/* 🆕 担当者制限表示 */}
                    {maxContacts !== 'unlimited' && (
                      <p className="text-xs text-gray-500">
                        {selectedClient.contacts.length} / {maxContacts}
                        {isContactLimitReached && (
                          <span className="ml-1 text-amber-600">（上限）</span>
                        )}
                      </p>
                    )}
                  </div>
                  {/* 🆕 担当者追加ボタン（制限時はグレー） */}
                  <button 
                    onClick={handleAddContact}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm flex items-center gap-2 ${
                      isContactLimitReached
                        ? 'bg-gray-300 text-gray-500 hover:bg-gray-400'
                        : 'bg-green-500 text-white hover:bg-green-600'
                    }`}
                  >
                    {isContactLimitReached ? <Lock className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    担当者追加
                  </button>
                </div>

                {selectedClient.contacts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <User className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">担当者が登録されていません</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedClient.contacts.map((contact) => (
                      <div
                        key={contact.id}
                        className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-gray-900">{contact.name}</h4>
                              {contact.title && (
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                  {contact.title}
                                </span>
                              )}
                            </div>
                            {contact.department && (
                              <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                                <Briefcase className="w-3 h-3" />
                                {contact.department}
                              </p>
                            )}
                            {contact.email && (
                              <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                                <Mail className="w-3 h-3" />
                                <a href={`mailto:${contact.email}`} className="hover:text-blue-600">
                                  {contact.email}
                                </a>
                              </p>
                            )}
                            {contact.phone && (
                              <p className="text-sm text-gray-600 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                <a href={`tel:${contact.phone}`} className="hover:text-blue-600">
                                  {contact.phone}
                                </a>
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button 
                              onClick={() => handleEditContact(contact)}
                              className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            >
                              編集
                            </button>
                            <button 
                              onClick={() => handleDeleteContact(contact.id)}
                              className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            >
                              削除
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full py-20">
              <div className="text-center text-gray-500">
                <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">取引先を選択してください</p>
                <p className="text-sm mt-2">左のリストから取引先をクリック</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* モーダル */}
      <ClientModal
        isOpen={isClientModalOpen}
        onClose={() => {
          setIsClientModalOpen(false);
          setEditingClient(undefined);
        }}
        onSubmit={handleClientSubmit}
        client={editingClient}
        isProcessing={isProcessing}
      />

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => {
          setIsContactModalOpen(false);
          setEditingContact(undefined);
        }}
        onSubmit={handleContactSubmit}
        contact={editingContact}
        clientId={selectedClient?.id || ''}
        clientName={selectedClient?.name || ''}
        isProcessing={isProcessing}
      />

      {/* 🆕 アップグレードモーダル */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
      />
    </div>
  );
};
